import { INDEMNITE_PRECARITE_CDD_OPTION } from '@shared/constants/Taux';
import { SecureTenantStore } from '@infra/persistence/SecureTenantStore';

export type MotifSortieStc = 'demission' | 'licenciement' | 'rupture' | 'fin_cdd';

export interface StcBaseConfig {
  salaire_base: boolean;
  sursalaire: boolean;
  prime_anciennete: boolean;
  transport: boolean;
  logement: boolean;
  fonction_ind: boolean;
  autres: boolean;
}

export interface StcLicenciementTranche {
  jusqua: number;
  taux: number;
}

export interface StcLicenciementConfig {
  anciennete_min_annees: number;
  annees_entieres: boolean;
  tranches: StcLicenciementTranche[];
}

/**
 * `anciennete_art82` : préavis légal par ancienneté (Code du travail art. 82) — défaut.
 * `categorie` : durées fixes par catégorie ci-dessous (option conventionnelle).
 */
export type PreavisMode = 'anciennete_art82' | 'categorie';

export interface StcPreavisConfig {
  mode: PreavisMode;
  employe: number;
  maitrise: number;
  cadre: number;
  indemnise_si_non_effectue: boolean;
}

export interface StcCongesConfig {
  jours_par_an: number;
  base_jours: number;
  deduire_conges_pris: boolean;
  report_multi_annees: boolean;
  report_plafond_jours: number;
}

/** Prime de précarité CDD : AUCUNE base légale (art. 60) ; option conventionnelle, désactivée par défaut. */
export interface StcPrecariteConfig {
  actif: boolean;
  taux: number;
}

export interface StcTreiziemeConfig {
  actif: boolean;
  prorata: boolean;
}

/** Paramètres du solde de tout compte, ajustables par convention. */
export interface StcConfig {
  convention: string;
  base: StcBaseConfig;
  licenciement: StcLicenciementConfig;
  preavis: StcPreavisConfig;
  conges: StcCongesConfig;
  precarite_cdd: StcPrecariteConfig;
  treizieme: StcTreiziemeConfig;
}

/** @deprecated Format v2.3 — migré automatiquement vers StcConfig complet. */
interface LegacyStcConfig {
  taux_licenciement?: number;
  taux_precarite_cdd?: number;
  diviseur_conges?: number;
}

export const DEFAULT_STC_CONFIG: StcConfig = {
  convention: 'Code du travail gabonais (loi 022/2021)',
  base: {
    salaire_base: true,
    sursalaire: true,
    prime_anciennete: true,
    transport: false,
    logement: false,
    fonction_ind: true,
    autres: false,
  },
  // Code du travail art. 87 et 90 : 20 % du salaire mensuel moyen par année de présence, SANS
  // ancienneté minimale, fractions d'année comprises (minimum obligatoire). Trois tranches
  // identiques pour rester éditables dans l'écran Paramètres.
  licenciement: {
    anciennete_min_annees: 0,
    annees_entieres: false,
    tranches: [
      { jusqua: 5, taux: 0.2 },
      { jusqua: 10, taux: 0.2 },
      { jusqua: 999, taux: 0.2 },
    ],
  },
  // Art. 82 : préavis par ancienneté (les durées par catégorie ne servent qu'en mode `categorie`).
  preavis: {
    mode: 'anciennete_art82',
    employe: 1,
    maitrise: 2,
    cadre: 3,
    indemnise_si_non_effectue: true,
  },
  conges: {
    jours_par_an: 24,
    base_jours: 30,
    deduire_conges_pris: true,
    report_multi_annees: true,
    report_plafond_jours: 0,
  },
  precarite_cdd: { actif: false, taux: INDEMNITE_PRECARITE_CDD_OPTION },
  treizieme: { actif: false, prorata: true },
};

const NAMESPACE = 'rh.stc_config';
const CONVENTION_KEY_NS = 'rh.convention_key';

function legacyKey(tenantId: string): string {
  return `nexxus.stc.${tenantId}`;
}

function clampRate(value: unknown, fallback: number): number {
  const n = typeof value === 'number' ? value : Number(value);
  if (!Number.isFinite(n) || n < 0 || n > 1) return fallback;
  return n;
}

function clampNonNegInt(value: unknown, fallback: number, max = 999): number {
  const n = typeof value === 'number' ? value : Number(value);
  if (!Number.isFinite(n) || n < 0) return fallback;
  return Math.min(max, Math.round(n));
}

function clampPosInt(value: unknown, fallback: number, max = 31): number {
  const n = typeof value === 'number' ? value : Number(value);
  if (!Number.isFinite(n) || n < 1) return fallback;
  return Math.min(max, Math.round(n));
}

function clampBool(value: unknown, fallback: boolean): boolean {
  return typeof value === 'boolean' ? value : fallback;
}

function isLegacyStcConfig(raw: unknown): raw is LegacyStcConfig {
  return (
    typeof raw === 'object' &&
    raw !== null &&
    ('taux_licenciement' in raw || 'diviseur_conges' in raw) &&
    !('licenciement' in raw)
  );
}

function migrateLegacyConfig(raw: LegacyStcConfig): StcConfig {
  const taux = clampRate(raw.taux_licenciement, 0.2);
  const diviseur = clampPosInt(raw.diviseur_conges, 26, 31);
  return {
    ...DEFAULT_STC_CONFIG,
    licenciement: {
      ...DEFAULT_STC_CONFIG.licenciement,
      tranches: DEFAULT_STC_CONFIG.licenciement.tranches.map((tr) => ({ ...tr, taux })),
    },
    conges: {
      ...DEFAULT_STC_CONFIG.conges,
      base_jours: diviseur,
      jours_par_an: DEFAULT_STC_CONFIG.conges.jours_par_an,
    },
    precarite_cdd: {
      actif: false,
      taux: clampRate(raw.taux_precarite_cdd, INDEMNITE_PRECARITE_CDD_OPTION),
    },
  };
}

function mergeStcPartial(defaults: StcConfig, user: Partial<StcConfig>): StcConfig {
  const cfg: StcConfig = JSON.parse(JSON.stringify(defaults)) as StcConfig;
  if (user.convention !== undefined) cfg.convention = String(user.convention);
  if (user.base) cfg.base = { ...cfg.base, ...user.base };
  if (user.licenciement) {
    cfg.licenciement = { ...cfg.licenciement, ...user.licenciement };
    if (user.licenciement.tranches) {
      cfg.licenciement.tranches = user.licenciement.tranches.map((tr, i) => ({
        jusqua: clampNonNegInt(tr.jusqua, defaults.licenciement.tranches[i]?.jusqua ?? 999),
        taux: clampRate(tr.taux, defaults.licenciement.tranches[i]?.taux ?? 0.2),
      }));
    }
  }
  if (user.preavis) cfg.preavis = { ...cfg.preavis, ...user.preavis };
  if (user.conges) cfg.conges = { ...cfg.conges, ...user.conges };
  if (user.precarite_cdd) cfg.precarite_cdd = { ...cfg.precarite_cdd, ...user.precarite_cdd };
  if (user.treizieme) cfg.treizieme = { ...cfg.treizieme, ...user.treizieme };
  return cfg;
}

export function normalizeStcConfig(
  raw: Partial<StcConfig> | LegacyStcConfig | null | undefined,
): StcConfig {
  if (isLegacyStcConfig(raw)) return normalizeStcConfig(migrateLegacyConfig(raw));
  const merged = mergeStcPartial(DEFAULT_STC_CONFIG, raw ?? {});
  return {
    convention: merged.convention || DEFAULT_STC_CONFIG.convention,
    base: {
      salaire_base: merged.base.salaire_base !== false,
      sursalaire: clampBool(merged.base.sursalaire, DEFAULT_STC_CONFIG.base.sursalaire),
      prime_anciennete: clampBool(
        merged.base.prime_anciennete,
        DEFAULT_STC_CONFIG.base.prime_anciennete,
      ),
      transport: clampBool(merged.base.transport, DEFAULT_STC_CONFIG.base.transport),
      logement: clampBool(merged.base.logement, DEFAULT_STC_CONFIG.base.logement),
      fonction_ind: clampBool(merged.base.fonction_ind, DEFAULT_STC_CONFIG.base.fonction_ind),
      autres: clampBool(merged.base.autres, DEFAULT_STC_CONFIG.base.autres),
    },
    licenciement: {
      anciennete_min_annees: clampNonNegInt(
        merged.licenciement.anciennete_min_annees,
        DEFAULT_STC_CONFIG.licenciement.anciennete_min_annees,
      ),
      annees_entieres: clampBool(
        merged.licenciement.annees_entieres,
        DEFAULT_STC_CONFIG.licenciement.annees_entieres,
      ),
      tranches: (merged.licenciement.tranches?.length
        ? merged.licenciement.tranches
        : DEFAULT_STC_CONFIG.licenciement.tranches
      ).map((tr, i) => ({
        jusqua: clampNonNegInt(
          tr.jusqua,
          DEFAULT_STC_CONFIG.licenciement.tranches[i]?.jusqua ?? 999,
        ),
        taux: clampRate(tr.taux, DEFAULT_STC_CONFIG.licenciement.tranches[i]?.taux ?? 0.2),
      })),
    },
    preavis: {
      mode: merged.preavis.mode === 'categorie' ? 'categorie' : 'anciennete_art82',
      employe: clampNonNegInt(merged.preavis.employe, DEFAULT_STC_CONFIG.preavis.employe, 24),
      maitrise: clampNonNegInt(merged.preavis.maitrise, DEFAULT_STC_CONFIG.preavis.maitrise, 24),
      cadre: clampNonNegInt(merged.preavis.cadre, DEFAULT_STC_CONFIG.preavis.cadre, 24),
      indemnise_si_non_effectue: clampBool(
        merged.preavis.indemnise_si_non_effectue,
        DEFAULT_STC_CONFIG.preavis.indemnise_si_non_effectue,
      ),
    },
    conges: {
      jours_par_an: clampNonNegInt(
        merged.conges.jours_par_an,
        DEFAULT_STC_CONFIG.conges.jours_par_an,
        365,
      ),
      base_jours: clampPosInt(merged.conges.base_jours, DEFAULT_STC_CONFIG.conges.base_jours),
      deduire_conges_pris: clampBool(
        merged.conges.deduire_conges_pris,
        DEFAULT_STC_CONFIG.conges.deduire_conges_pris,
      ),
      report_multi_annees: clampBool(
        merged.conges.report_multi_annees,
        DEFAULT_STC_CONFIG.conges.report_multi_annees,
      ),
      report_plafond_jours: clampNonNegInt(
        merged.conges.report_plafond_jours,
        DEFAULT_STC_CONFIG.conges.report_plafond_jours,
      ),
    },
    precarite_cdd: {
      actif: clampBool(merged.precarite_cdd.actif, DEFAULT_STC_CONFIG.precarite_cdd.actif),
      taux: clampRate(merged.precarite_cdd.taux, DEFAULT_STC_CONFIG.precarite_cdd.taux),
    },
    treizieme: {
      actif: clampBool(merged.treizieme.actif, DEFAULT_STC_CONFIG.treizieme.actif),
      prorata: clampBool(merged.treizieme.prorata, DEFAULT_STC_CONFIG.treizieme.prorata),
    },
  };
}

export async function loadStcConfig(tenantId: string): Promise<StcConfig> {
  const stored = await SecureTenantStore.getJson<Partial<StcConfig> | LegacyStcConfig>(
    tenantId,
    NAMESPACE,
    DEFAULT_STC_CONFIG,
    legacyKey(tenantId),
  );
  return normalizeStcConfig(stored);
}

export async function saveStcConfig(tenantId: string, config: StcConfig): Promise<StcConfig> {
  const normalized = normalizeStcConfig(config);
  await SecureTenantStore.setJson(tenantId, NAMESPACE, normalized, legacyKey(tenantId));
  return normalized;
}

export async function loadConventionKey(tenantId: string): Promise<string> {
  return SecureTenantStore.getJson<string>(tenantId, CONVENTION_KEY_NS, 'general');
}

export async function saveConventionKey(tenantId: string, key: string): Promise<void> {
  await SecureTenantStore.setJson(tenantId, CONVENTION_KEY_NS, key);
}
